export type TrackId = "drums" | "keys" | "bass" | "lead";
export type KeyboardTrackId = "keys" | "bass" | "lead";

export type PitchClass =
  | "C"
  | "C#"
  | "D"
  | "D#"
  | "E"
  | "F"
  | "F#"
  | "G"
  | "G#"
  | "A"
  | "A#"
  | "B";

export const PITCH_CLASSES: readonly PitchClass[] = [
  "C",
  "C#",
  "D",
  "D#",
  "E",
  "F",
  "F#",
  "G",
  "G#",
  "A",
  "A#",
  "B",
];

export interface ScaleOptions {
  root: PitchClass;
  blueFlat3: boolean;
  blueFlat5: boolean;
}

export interface ScaleNote {
  midi: number;
  label: string;
  isBlue: boolean;
}

const MAJOR_SEMITONES = [0, 2, 4, 5, 7, 9, 11, 12];

const TRACK_OCTAVE: Record<KeyboardTrackId, number> = {
  bass: 3,
  keys: 4,
  lead: 5,
};

export function rootMidiFor(track: KeyboardTrackId, root: PitchClass): number {
  const octave = TRACK_OCTAVE[track];
  const pitchIndex = PITCH_CLASSES.indexOf(root);
  return 12 * (octave + 1) + pitchIndex;
}

export function pitchClassLabel(midi: number): string {
  return PITCH_CLASSES[((midi % 12) + 12) % 12];
}

export function midiLabel(midi: number): string {
  const octave = Math.floor(midi / 12) - 1;
  return `${pitchClassLabel(midi)}${octave}`;
}

export function buildScale(
  track: KeyboardTrackId,
  opts: ScaleOptions,
): ScaleNote[] {
  const rootMidi = rootMidiFor(track, opts.root);
  const offsets = [...MAJOR_SEMITONES];
  const blueSet = new Set<number>();
  if (opts.blueFlat3) {
    offsets.push(3);
    blueSet.add(3);
  }
  if (opts.blueFlat5) {
    offsets.push(6);
    blueSet.add(6);
  }
  offsets.sort((a, b) => b - a);
  return offsets.map((offset) => ({
    midi: rootMidi + offset,
    label: midiLabel(rootMidi + offset),
    isBlue: blueSet.has(offset),
  }));
}

export function scaleRowCount(opts: {
  blueFlat3: boolean;
  blueFlat5: boolean;
}): number {
  return (
    MAJOR_SEMITONES.length + (opts.blueFlat3 ? 1 : 0) + (opts.blueFlat5 ? 1 : 0)
  );
}

/**
 * Semitone offsets (high → low) of the scale for the given blue-note config.
 * Independent of root pitch — root just shifts the whole scale by a constant.
 * Useful for mapping melody-grid rows between two blue-note configurations
 * without dropping armed cells.
 */
export function scaleSemitonesDesc(opts: {
  blueFlat3: boolean;
  blueFlat5: boolean;
}): number[] {
  const offsets = [...MAJOR_SEMITONES];
  if (opts.blueFlat3) offsets.push(3);
  if (opts.blueFlat5) offsets.push(6);
  offsets.sort((a, b) => b - a);
  return offsets;
}
